/**
 * AR Vision Demo — hook-shim helper.
 *
 * THROWAWAY DEMO CODE. In demo mode the AR query hooks short-circuit to return
 * fixture data instead of the backend's. We do this WITHOUT skipping the real
 * `useQuery` call (Rules of Hooks): each hook always calls `useQuery`, then
 * pipes the result through `demoQueryResult()`, which — only when demo mode is
 * on — replaces the query's `data`/loading/error fields with a synchronous
 * fixture value.
 *
 * Because demo mode also disables the query (`enabled: false` is layered in by
 * each shimmed hook), no network request fires; the override purely supplies a
 * resolved-success shape so real pages/components render unchanged.
 *
 * The returned object keeps the same reference type as a TanStack `useQuery`
 * result so the spread (`...query`) in the real hooks stays type-correct.
 */

import type { UseQueryResult } from '@tanstack/react-query';
import { isArDemoMode } from './demo-mode';
import { useArDemoStore } from './fixture-store';
import type { ArDemoFixtureState } from './fixture-store.types';

/** True when the AR query hooks should serve fixtures. */
export function arDemoActive(): boolean {
  return isArDemoMode();
}

/**
 * Reactively select a fixture slice when demo mode is on. Returns `undefined`
 * when demo mode is off so the caller can fall through to the real query.
 *
 * Always calls the underlying store hook (Rules of Hooks); the selector simply
 * returns `undefined` when demo mode is off.
 */
export function useArDemoFixture<T>(
  selector: (s: ArDemoFixtureState) => T,
): T | undefined {
  return useArDemoStore((s) => (isArDemoMode() ? selector(s) : undefined));
}

/**
 * Wrap a real `useQuery` result so that, when demo mode is on, it presents the
 * provided fixture `data` as a resolved success. When demo mode is off the
 * original query result passes through untouched.
 */
export function withArDemoData<TData, TError>(
  query: UseQueryResult<TData, TError>,
  fixtureData: TData | undefined,
): UseQueryResult<TData, TError> {
  if (!isArDemoMode() || fixtureData === undefined) {
    return query;
  }

  return {
    ...query,
    data: fixtureData,
    error: null,
    isError: false,
    isLoading: false,
    isLoadingError: false,
    isPending: false,
    isSuccess: true,
    isFetched: true,
    isFetchedAfterMount: true,
    isFetching: false,
    isRefetching: false,
    isPlaceholderData: false,
    status: 'success',
    fetchStatus: 'idle',
  } as UseQueryResult<TData, TError>;
}
